import React, { useEffect, useState } from "react";
import { ICustoTriagem } from "../../types/types";
import { FrontendNotification } from "../../../../../../shared/Notification";
import api from "../../../../../../services/api";
import SelectCustom from "../../../../../../components/SelectCustom";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";

interface Option {
  value: number;
  label: string;
}

interface ITransportadoraData {
  id_transportadora: number;
  razao_social: string;
}

const tipoVeiculoOptions: Option[] = [
  { value: 1, label: "Truck" },
  { value: 2, label: "Carreta" },
  { value: 3, label: "Bitrem" },
];

const tipoCargaOptions: Option[] = [
  { value: 1, label: "Carga Geral" },
  { value: 2, label: "Combustível" },
  { value: 3, label: "Conteiner" },
  { value: 4, label: "Gás" },
];

const tipoOperacaoOptions: Option[] = [
  { value: 1, label: "Carga" },
  { value: 2, label: "Descarga" }
];

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: ICustoTriagem;
  onConfirm: () => void;
  onClose: () => void;
}

const Form: React.FC<Props> = ({ isEdit, selectedRow, onConfirm, onClose }) => {
  const [tipoVeiculo, setTipoVeiculo] = useState<number>(selectedRow?.tipo_veiculo ?? 1);
  const [tipoCarga, setTipoCarga] = useState<number>(selectedRow?.tipo_carga ?? 1);
  const [tipoOperacao, setTipoOperacao] = useState<number>(selectedRow?.tipo_operacao ?? 1);
  const [tempoBase, setTempoBase] = useState<string>(String(selectedRow?.tempo_base_triagem ?? 0));
  const [custoBase, setCustoBase] = useState<string>(String(selectedRow?.custo_base_triagem ?? 0));
  const [idTransportadora, setIdTransportadora] = useState<number | undefined>(selectedRow?.id_transportadora ?? undefined);
  const [transportadorasOptions, setTransportadorasOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const formatCurrency = (value: string): string => {
    // Remove tudo que não for número
    const onlyNumbers = value.replace(/\D/g, '');
    
    if (!onlyNumbers || onlyNumbers === '0') return '';
    
    // Converte para número dividindo por 100 (para centavos)
    const numberValue = parseFloat(onlyNumbers) / 100;
    
    // Formata como moeda brasileira
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(numberValue);
  };


  useEffect(() => {
    const fetchTransportadoras = async () => {
      try {
        const body = {
          qtd_por_pagina: 100,
          order_by: "data_historico",
          order_direction: "desc",
        };

        const response = await api.post("/listar/transportadoras", body);

        const mappingData = response.data.data.map((rows: ITransportadoraData) => {
          return {
            value: rows.id_transportadora,
            label: rows.razao_social,
          };
        });

        setTransportadorasOptions(mappingData);
      } catch (error) {
        console.error("Erro ao buscar transportadoras:", error);
        FrontendNotification("Erro ao carregar transportadoras", "error");
      }
    };
    fetchTransportadoras();
  }, []);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    try {
      setLoading(true);
      
      const body: Partial<ICustoTriagem> = {
        tipo_veiculo: tipoVeiculo,
        tipo_carga: tipoCarga,
        tipo_operacao: tipoOperacao,
        tempo_base_triagem: Number(tempoBase),
        custo_base_triagem: Number(custoBase) / 100, // Converte centavos para reais
        id_transportadora: idTransportadora ?? undefined,
        id_usuario_historico: user?.id,
      };

      if (isEdit && selectedRow?.id_custo_triagem) {
        body.id_custo_triagem = selectedRow.id_custo_triagem;
        await api.post("/editar/custoTriagem", body);
        FrontendNotification("Custo de triagem atualizado com sucesso", "success");
      } else {
        await api.post("/cadastrar/custoTriagem", body);
        FrontendNotification("Custo de triagem salvo com sucesso", "success");
      }
      onConfirm();
      setLoading(false);
      onClose();
    } catch {
      setLoading(false);
      FrontendNotification("Erro ao salvar custo de triagem", "error");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6">
      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col">
          <SelectCustom
            title="Tipo Veículo"
            data={tipoVeiculoOptions}
            onChange={(selectedOption: Option) => setTipoVeiculo(selectedOption.value)}
            value={tipoVeiculo}
          />
        </label>

        <label className="flex flex-col">
          <SelectCustom
            title="Tipo Carga"
            data={tipoCargaOptions}
            onChange={(selectedOption: Option) => setTipoCarga(selectedOption.value)}
            value={tipoCarga}
          />
        </label>

        <label className="flex flex-col">
          <SelectCustom
            title="Tipo Operação"
            data={tipoOperacaoOptions}
            onChange={(selectedOption: Option) => setTipoOperacao(selectedOption.value)}
            value={tipoOperacao}
          />
        </label>

        <label className="flex flex-col">
          <span className="text-sm mb-1">Tempo Base (Horas)</span>
          <input type="text" value={tempoBase} onChange={(e) => {
            const value = e.target.value;
            if (value === '' || (/^\d+$/.test(value) && Number(value) >= 0)) {
              setTempoBase(value);
            }
          }} className="border bg-white px-4 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-[#062D4E]" min="0" />
        </label>

        <label className="flex flex-col">
          <span className="text-sm mb-1">Custo Base (R$)</span>
          <input 
            type="text" 
            value={formatCurrency(custoBase)} 
            onChange={(e) => {
              const inputValue = e.target.value;
              // Permite apenas números sendo digitados
              const onlyNumbers = inputValue.replace(/\D/g, '');
              setCustoBase(onlyNumbers);
            }} 
            className="border bg-white px-4 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-[#062D4E]"
            placeholder="R$ 0,00"
          />
        </label>

        <label className="flex flex-col">
          <SelectCustom
            title="Transportadora (opcional)"
            data={transportadorasOptions}
            onChange={(selectedOption: Option) => setIdTransportadora(selectedOption.value)}
            value={idTransportadora}
          />
        </label>
      </div>

      <div className="flex justify-end mt-6 gap-2">
        <button type="button" onClick={onClose} className="px-6 py-2 rounded-full border border-[#062D4E] text-[#062D4E] bg-white hover:bg-[#F0F4F8] transition">Cancelar</button>
        <button type="submit" disabled={loading} className="px-6 py-2 rounded-full bg-[#062D4E] text-white hover:bg-[#094077] transition">{loading ? "Salvando..." : "Salvar"}</button>
      </div>
    </form>
  );
};

export default Form;
